<form [formGroup]="form" class="flex flex-col space-y-2">
    @if (validationResult) {
        <app-validation-error-list
            [validationResult]="validationResult"
        ></app-validation-error-list>
    }

    <mat-accordion>
        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="!text-tertiary-900 font-normal text-xl py-2"
                >
                    <div class="flex space-x-2">
                        <h1
                            class="!text-tertiary-900 font-normal !text-xl !m-0"
                        >
                            Frequency Pool
                        </h1>

                        @if (isFrequencyPoolSectionInvalid()) {
                            <img
                                src="assets/svg/error_icon.svg"
                                alt="Error Icon"
                            />
                        }
                    </div>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex flex-col space-y-2">
                <div class="step-container">
                    <p class="step-label">Step 1:</p>
                    <app-application-form-field
                        label="Select a Single Frequency Pool"
                        [showTooltipIcon]="false"
                        class="w-full !mt-0"
                    >
                        <mat-form-field
                            appearance="outline"
                            class="w-full !-mb-[1.25em]"
                            color="primary"
                        >
                            <mat-label
                                >Select a Single Frequency Pool</mat-label
                            >
                            <mat-select formControlName="applicationTypeCode">
                                @for (
                                    option of applicationTypes;
                                    track option.value
                                ) {
                                    <mat-option [value]="option.value">{{
                                        option.label
                                    }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    </app-application-form-field>
                </div>

                <div class="step-container">
                    <p class="step-label">Step 2 (Optional):</p>
                    <app-application-form-field
                        label="Select a Frequency Type"
                        [showTooltipIcon]="false"
                        class="w-full !mt-0"
                    >
                        <mat-form-field
                            appearance="outline"
                            class="w-full !-mb-[1.25em]"
                            color="primary"
                        >
                            <mat-label>Select a Frequency Type</mat-label>
                            <mat-select
                                formControlName="frequencyAllocationCode"
                            >
                                @for (
                                    option of frequencyAllocationCodes;
                                    track option.value
                                ) {
                                    <mat-option [value]="option.value">{{
                                        option.label
                                    }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    </app-application-form-field>
                </div>

                <app-grid-toolbar
                    [gridApi]="
                        gridApis[coordinationParametersGridType.frequencyPool]
                    "
                    [options]="frequencyPoolGridToolbarOptions"
                ></app-grid-toolbar>
                <ag-grid-angular
                    [context]="
                        initializeGridContext(
                            coordinationParametersGridType.frequencyPool
                        )
                    "
                    [rowData]="
                        rowData[coordinationParametersGridType.frequencyPool]
                    "
                    [gridOptions]="frequencyPoolGridOptions"
                    (gridReady)="
                        onGridReady(
                            $event,
                            coordinationParametersGridType.frequencyPool
                        )
                    "
                    class="ag-theme-quartz h-[370px] w-full"
                >
                </ag-grid-angular>
            </div>
        </mat-expansion-panel>
    </mat-accordion>

    <mat-accordion>
        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="!text-tertiary-900 font-normal text-xl py-2"
                >
                    <div class="flex space-x-2">
                        <h1
                            class="!text-tertiary-900 font-normal !text-xl !m-0"
                        >
                            Include/Exclude
                        </h1>

                        @if (
                            isIncludeFrequenciesForValidationSectionInvalid()
                        ) {
                            <img
                                src="assets/svg/error_icon.svg"
                                alt="Error Icon"
                            />
                        }
                    </div>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <app-grid-toolbar
                [gridApi]="
                    gridApis[
                        coordinationParametersGridType
                            .includeFrequenciesForValidation
                    ]
                "
                [options]="includeFrequenciesForValidationGridToolbarOptions"
            ></app-grid-toolbar>

            <ag-grid-angular
                [context]="
                    initializeGridContext(
                        coordinationParametersGridType.includeFrequenciesForValidation
                    )
                "
                [rowData]="
                    rowData[
                        coordinationParametersGridType
                            .includeFrequenciesForValidation
                    ]
                "
                [gridOptions]="includeFrequenciesForValidationGridOptions"
                (gridReady)="
                    onGridReady(
                        $event,
                        coordinationParametersGridType.includeFrequenciesForValidation
                    )
                "
                class="ag-theme-quartz h-[370px] w-full"
            >
            </ag-grid-angular>
        </mat-expansion-panel>
    </mat-accordion>

    <mat-accordion>
        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="!text-tertiary-900 font-normal text-xl py-2"
                >
                    <div class="flex space-x-2">
                        <h1
                            class="!text-tertiary-900 font-normal !text-xl !m-0"
                        >
                            Selection of Friendlies
                        </h1>
                    </div>
                </mat-panel-title>

                <mat-panel-description>
                    List out Call sign(s), ULS File number(s) that need to be
                    treated as friendlies while coordinating. Enter each item
                    separately or use a comma and/or new line separated list.
                </mat-panel-description>
            </mat-expansion-panel-header>

            <form
                [formGroup]="friendlySelectionSearchForm"
                class="mt-3 flex flex-col space-y-4 xl:grid xl:grid-cols-2 xl:gap-6 xl:space-y-0"
            >
                <div class="flex flex-col space-y-2 xl:col-span-1">
                    <div class="flex flex-col space-y-2">
                        <div class="space-y-1">
                            <h2
                                class="text-xl !font-normal !m-0 text-tertiary-900"
                            >
                                FRN(s) to Search:
                            </h2>
                            <p class="!m-0 text-tertiary-900 text-sm">
                                You may lookup all Call sign(s) and active file
                                number(s) by FRN:
                            </p>
                        </div>

                        <div class="flex flex-col">
                            <mat-form-field
                                appearance="outline"
                                class="w-full !mb-[-1rem]"
                            >
                                <mat-label>FRN(s) Search</mat-label>
                                <textarea
                                    title="FRN(s) Search"
                                    formControlName="frnSearch"
                                    matInput
                                    [appRegexInput]="
                                        regExPattern.NumericOnlyWithComma
                                    "
                                    class="!resize-none !h-6"
                                ></textarea>
                                <mat-hint class="2xl:w-60p text-wrap">{{
                                    getFRNSearchHint()
                                }}</mat-hint>
                                @if (getFRNSearchError(); as error) {
                                    <mat-error class="2xl:w-60p text-wrap">{{
                                        error
                                    }}</mat-error>
                                }
                            </mat-form-field>

                            <div
                                class="flex flex-col-reverse space-y-reverse space-y-2 justify-end 2xl:flex-row 2xl:space-x-2 2xl:mt-0"
                                [class.mt-5]="
                                    getFRNSearchHint() || getFRNSearchError()
                                "
                            >
                                @if (!isReadOnly()) {
                                    <button
                                        type="button"
                                        mat-stroked-button
                                        color="primary"
                                        class="!border-primary-700 !min-w-min !h-8 !px-3"
                                        tabindex="-1"
                                        (click)="clearFRNSearchHistoryList()"
                                    >
                                        Clear FRN List
                                    </button>

                                    <button
                                        title="Search"
                                        mat-flat-button
                                        color="primary"
                                        class="!min-w-min !h-8 !px-3"
                                        (click)="searchFRNFriendlies()"
                                    >
                                        Search
                                    </button>
                                }
                            </div>

                            <div
                                class="flex flex-wrap gap-2 overflow-y-auto max-h-44 mt-3"
                                tabindex="-1"
                            >
                                @for (
                                    searchedFrn of friendlySelectionSearchFormFRNSearchHistoryControl.value;
                                    track searchedFrn
                                ) {
                                    <mat-chip-row
                                        primary
                                        class="rounded-none bg-primary-100 text-tertiary-900 w-28 text-center"
                                    >
                                        @if (
                                            searchedFrn.frn &&
                                            searchedFrn.frn.length > 10
                                        ) {
                                            <span title="{{ searchedFrn.frn }}"
                                                >{{
                                                    searchedFrn.frn.slice(
                                                        0,
                                                        10
                                                    )
                                                }}...</span
                                            >
                                        } @else {
                                            <span>{{
                                                formatFrnForDisplay(
                                                    searchedFrn.frn
                                                )
                                            }}</span>
                                        }
                                    </mat-chip-row>
                                }
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col space-y-2">
                        <h2 class="text-xl !font-normal !m-0 text-tertiary-900">
                            Call Sign or ULS File Number:
                        </h2>
                        <div class="flex flex-col">
                            <mat-form-field
                                appearance="outline"
                                class="w-full !mb-[-1rem]"
                            >
                                <mat-label>Search</mat-label>
                                <textarea
                                    title="Search by Call Sign or ULS File Number"
                                    matInput
                                    formControlName="addCallSignOrULSFileNumber"
                                    (keyup.enter)="
                                        addCallSignsOrULSFileNumbersToFriendlies()
                                    "
                                    [appRegexInput]="
                                        regExPattern.AlphanumericWithComma
                                    "
                                    class="!resize-none !h-6"
                                ></textarea>
                                <mat-hint>{{
                                    getAddCallSignOrULSFileNumberHint()
                                }}</mat-hint>
                                @if (
                                    getAddCallSignOrULSFileNumberError();
                                    as error
                                ) {
                                    <mat-error>{{ error }}</mat-error>
                                }
                            </mat-form-field>
                            @if (!isReadOnly()) {
                                <button
                                    title="Add Call Sign or ULS File Number to Friendlies"
                                    mat-flat-button
                                    color="primary"
                                    class="!min-w-min !h-8 !px-3 ml-auto"
                                    (click)="
                                        addCallSignsOrULSFileNumbersToFriendlies()
                                    "
                                >
                                    Add
                                </button>
                            }
                        </div>
                    </div>
                </div>

                <div class="flex flex-col space-y-2 xl:col-span-1">
                    <app-grid-toolbar
                        [gridApi]="
                            gridApis[
                                coordinationParametersGridType.friendlySelection
                            ]
                        "
                        [options]="friendliesGridToolbarOptions"
                    ></app-grid-toolbar>
                    <ag-grid-angular
                        [context]="
                            initializeGridContext(
                                coordinationParametersGridType.friendlySelection
                            )
                        "
                        [rowData]="
                            rowData[
                                coordinationParametersGridType.friendlySelection
                            ]
                        "
                        [gridOptions]="friendlySelectionGridOptions"
                        (gridReady)="
                            onGridReady(
                                $event,
                                coordinationParametersGridType.friendlySelection
                            )
                        "
                        class="ag-theme-quartz h-[400px] w-full"
                    >
                    </ag-grid-angular>
                </div>
            </form>
        </mat-expansion-panel>
    </mat-accordion>
</form>
